import { cn } from "@/lib/utils";
import { Activity, Calendar, Zap } from "lucide-react";

interface CalendarCompactLegendProps {
  className?: string;
}

export const CalendarCompactLegend = ({ className }: CalendarCompactLegendProps) => {
  return (
    <div className={cn(
      "flex items-center justify-between gap-4 py-3 md:py-4 px-4 md:px-5 lg:px-6",
      "border-b border-slate-600/30",
      "bg-gradient-to-r from-slate-800/40 via-slate-800/30 to-slate-800/40",
      "backdrop-blur-md shadow-inner shadow-slate-900/20",
      className
    )}>
      {/* Enhanced volatility legend with premium styling */}
      <div className="flex items-center gap-3 md:gap-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-slate-700/60 to-slate-800/60 border border-slate-600/40">
            <Activity className="h-3.5 w-3.5 text-slate-300" />
          </div>
          <span className="text-slate-200 text-sm md:text-base font-semibold bg-gradient-to-r from-slate-200 to-slate-300 bg-clip-text text-transparent">
            Volatility:
          </span>
        </div>
        
        <div className="flex items-center gap-3 md:gap-4">
          <div className="flex items-center gap-1.5">
            {/* High volatility - 5 tall bars */}
            <div className="flex items-end gap-[1px] h-3.5">
              <div className="w-[2px] h-3 bg-gradient-to-t from-purple-500 to-purple-300 rounded-sm opacity-80" />
              <div className="w-[2px] h-3.5 bg-gradient-to-t from-purple-500 to-purple-300 rounded-sm" />
              <div className="w-[2px] h-2.5 bg-gradient-to-t from-purple-500 to-purple-300 rounded-sm opacity-80" />
              <div className="w-[2px] h-3.5 bg-gradient-to-t from-purple-500 to-purple-300 rounded-sm" />
              <div className="w-[2px] h-3 bg-gradient-to-t from-purple-500 to-purple-300 rounded-sm opacity-80" />
            </div>
            <span className="text-purple-300 text-xs md:text-sm font-medium">High</span>
          </div>
          
          <div className="flex items-center gap-1.5">
            {/* Medium volatility - 3 medium bars */}
            <div className="flex items-end gap-[1px] h-3.5">
              <div className="w-[2px] h-2 bg-gradient-to-t from-blue-500 to-blue-300 rounded-sm opacity-80" />
              <div className="w-[2px] h-2.5 bg-gradient-to-t from-blue-500 to-blue-300 rounded-sm" />
              <div className="w-[2px] h-2 bg-gradient-to-t from-blue-500 to-blue-300 rounded-sm opacity-80" />
            </div>
            <span className="text-blue-300 text-xs md:text-sm font-medium hidden sm:inline">Medium</span>
            <span className="text-blue-300 text-xs md:text-sm font-medium sm:hidden">Med</span>
          </div>
          
          <div className="flex items-center gap-1.5">
            {/* Low volatility - 2 short bars */}
            <div className="flex items-end gap-[1px] h-3.5">
              <div className="w-[2px] h-1.5 bg-gradient-to-t from-cyan-500 to-cyan-300 rounded-sm opacity-80" />
              <div className="w-[2px] h-2 bg-gradient-to-t from-cyan-500 to-cyan-300 rounded-sm" />
            </div>
            <span className="text-cyan-300 text-xs md:text-sm font-medium">Low</span>
          </div>
        </div>
      </div>
      
      {/* Enhanced indicators with premium design */}
      <div className="flex items-center gap-4 md:gap-5">
        {/* Premium Today indicator */}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 ring-2 ring-blue-300/50 shadow-md shadow-blue-500/30">
          </div>
          <span className="text-blue-200 text-xs md:text-sm font-medium hidden sm:inline">Today</span>
        </div>
      </div>
    </div>
  );
};
